import { createBrowserClient } from "@supabase/ssr";

import type { Database } from "@/types/supabase";

type BrowserClient = ReturnType<typeof createBrowserClient<Database>>;

function resolveEnv(varName: string | undefined, fallback?: string) {
  if (varName && varName.trim().length > 0) {
    return varName.trim();
  }
  return fallback;
}

/**
 * Global auth state cache to prevent redundant getUser/getSession calls
 * This prevents the "token refresh storm" where multiple components
 * each trigger their own refresh on page load
 */
interface AuthCache {
  user: any;
  session: any;
  timestamp: number;
  getUserPromise: Promise<any> | null;
  getSessionPromise: Promise<any> | null;
  // Error state to prevent retry storms on rate limiting
  lastError: Error | null;
  lastErrorTimestamp: number;
  // Soft-fail counter for "fatal" auth errors. We require N consecutive
  // fatal-classified errors before nuking cookies — the first one is often a
  // transient deploy / multi-tab refresh race (Supabase says "Invalid Refresh
  // Token" but the cookie is actually fine, just temporarily out-of-sync with
  // the rotated value held by another tab). Resets on any successful auth.
  consecutiveFatalErrors: number;
  firstFatalErrorTimestamp: number;
}

// Symbol key for window-level auth cache (survives HMR and module re-evaluation)
const AUTH_CACHE_KEY = '__buildforce_auth_cache__';

// Get or create auth cache (persisted on window in browser)
// This function is called on every access to ensure we always get the window-persisted version
function getAuthCache(): AuthCache {
  // In browser, always use window-persisted cache
  if (typeof window !== 'undefined') {
    let existing = (window as any)[AUTH_CACHE_KEY];
    if (!existing) {
      existing = {
        user: null,
        session: null,
        timestamp: 0,
        getUserPromise: null,
        getSessionPromise: null,
        lastError: null,
        lastErrorTimestamp: 0,
        consecutiveFatalErrors: 0,
        firstFatalErrorTimestamp: 0,
      };
      (window as any)[AUTH_CACHE_KEY] = existing;
    }
    // Backfill new fields for cache instances created before this code shipped
    // (window cache survives across deploys via the same window object).
    if (existing.consecutiveFatalErrors === undefined) {
      existing.consecutiveFatalErrors = 0;
      existing.firstFatalErrorTimestamp = 0;
    }
    return existing;
  }

  // SSR fallback - not persisted but won't cause issues
  return {
    user: null,
    session: null,
    timestamp: 0,
    getUserPromise: null,
    getSessionPromise: null,
    lastError: null,
    lastErrorTimestamp: 0,
    consecutiveFatalErrors: 0,
    firstFatalErrorTimestamp: 0,
  };
}

// Cache validity: 30 seconds
const AUTH_CACHE_TTL_MS = 30000;

// Error cooldown: 60 seconds - don't retry auth requests after rate limiting
const AUTH_ERROR_COOLDOWN_MS = 60000;

// Number of consecutive fatal auth errors required before nuking cookies +
// redirecting to /auth?reason=session_expired. The first fatal error is often
// a transient race (multi-tab refresh, deploy cookie sync, Supabase API blip)
// that resolves itself. Only sustained failures across this many attempts
// indicate a real session-end.
const FATAL_AUTH_ERROR_THRESHOLD = 2;

// Window inside which consecutive fatal errors must accumulate to trip the
// nuke. If errors are spread further apart than this, the counter resets —
// otherwise a long-running tab with two unrelated transient errors months
// apart would falsely log out.
const FATAL_AUTH_ERROR_WINDOW_MS = 60_000; // 1 minute

// Track if we've already logged auth caching status (for debugging)
let authCacheDebugLogged = false;

// Track if we're already handling a fatal auth error to prevent loops
let isHandlingFatalError = false;

/**
 * Detects if an error is a fatal auth error that requires re-login
 * These errors mean the refresh token is permanently invalid
 *
 * NOTE: `refresh_token_already_used` is intentionally NOT fatal — it means
 * a concurrent request already consumed the refresh token and rotated to a
 * fresh one. The correct behavior is to wait briefly, re-read cookies, and
 * retry — not to clear all state and force the user to re-login. Treating
 * it as fatal was the cause of forced logouts on every Vercel deploy
 * (multiple tabs / SSR boundaries race to refresh simultaneously after the
 * page reloads, the first succeeds, subsequent callers hit this benign
 * race condition).
 */
function isFatalAuthError(error: Error | null | { message?: string; status?: number }): boolean {
  if (!error) return false;
  const message = (error.message || '').toLowerCase();
  return (
    message.includes('invalid refresh token') ||
    message.includes('refresh token is invalid') ||
    message.includes('refresh token not found') ||
    message.includes('token has been revoked')
  );
}

/**
 * Detects if an error is a rate limit error (429)
 * These should trigger immediate cooldown to stop the storm
 */
function isRateLimitError(error: Error | null | { message?: string; status?: number; code?: string }): boolean {
  if (!error) return false;
  const message = (error.message || '').toLowerCase();
  const status = (error as any).status;
  const code = (error as any).code;
  return (
    status === 429 ||
    code === '429' ||
    message.includes('429') ||
    message.includes('rate limit') ||
    message.includes('too many requests') ||
    message.includes('request rate limit reached')
  );
}

/**
 * Records a fatal-classified auth error and decides whether to actually nuke
 * cookies, or treat this occurrence as a transient race and soft-fail with a
 * cooldown. The first FATAL_AUTH_ERROR_THRESHOLD - 1 fatal errors within the
 * window are absorbed silently — only sustained failures cross the threshold
 * and trigger the destructive cookie wipe + redirect.
 *
 * Why: deploy races + multi-tab token rotation can produce transient
 * "Invalid Refresh Token" responses even when the user genuinely has a
 * valid session (the race-loser tab's cached cookie is briefly stale until
 * the next refresh cycle picks up the rotated token from cookies). Nuking
 * on first occurrence forced re-login on every Vercel deploy.
 */
function recordFatalAuthError(error: Error) {
  const cache = getAuthCache();
  const now = Date.now();

  // Reset the counter if the previous fatal error is older than the window
  // (it was a one-off, not a sustained failure).
  if (
    cache.firstFatalErrorTimestamp > 0 &&
    now - cache.firstFatalErrorTimestamp > FATAL_AUTH_ERROR_WINDOW_MS
  ) {
    cache.consecutiveFatalErrors = 0;
    cache.firstFatalErrorTimestamp = 0;
  }

  cache.consecutiveFatalErrors += 1;
  if (cache.firstFatalErrorTimestamp === 0) {
    cache.firstFatalErrorTimestamp = now;
  }

  if (cache.consecutiveFatalErrors < FATAL_AUTH_ERROR_THRESHOLD) {
    // Soft-fail: this is likely a transient race. Enter cooldown so the next
    // call doesn't retry immediately, but keep cookies intact.
    cache.lastError = error;
    cache.lastErrorTimestamp = now;
    console.warn(
      `[auth-cache] Fatal-classified auth error (${cache.consecutiveFatalErrors}/${FATAL_AUTH_ERROR_THRESHOLD}) — likely transient race, NOT clearing cookies yet:`,
      error.message,
    );
    return;
  }

  // Threshold crossed — this is a real session-end. Nuke and redirect.
  console.error(
    `[auth-cache] Fatal auth error threshold reached (${cache.consecutiveFatalErrors} consecutive failures within ${FATAL_AUTH_ERROR_WINDOW_MS / 1000}s) — clearing cookies and redirecting`,
  );
  handleFatalAuthError(error);
}

/**
 * Handles fatal auth errors by clearing all auth state and redirecting to login
 * This prevents infinite retry loops when the refresh token is permanently invalid
 */
function handleFatalAuthError(error: Error) {
  // Prevent concurrent handling
  if (isHandlingFatalError) {
    console.warn('[auth-cache] Already handling fatal error, skipping');
    return;
  }
  isHandlingFatalError = true;

  console.error('[auth-cache] Fatal auth error detected, clearing all auth state:', error.message);

  try {
    // Clear our auth cache
    const cache = getAuthCache();
    cache.user = null;
    cache.session = null;
    cache.timestamp = 0;
    cache.getUserPromise = null;
    cache.getSessionPromise = null;
    cache.lastError = error;
    cache.lastErrorTimestamp = Date.now();

    // Clear Supabase's localStorage entries
    if (typeof window !== 'undefined' && window.localStorage) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('sb-'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        console.log('[auth-cache] Clearing localStorage key:', key);
        localStorage.removeItem(key);
      });
    }

    // CRITICAL: Also clear Supabase cookies (SSR stores tokens in cookies, not localStorage!)
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        if (cookieName.includes('sb-') || cookieName.includes('supabase')) {
          console.log('[auth-cache] Clearing cookie:', cookieName);
          // Clear cookie by setting expiry in the past, for multiple possible paths/domains
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
          // Also try with buildforce.io domain for production
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.buildforce.io`;
        }
      });
    }

    // Redirect to login page after a brief delay to allow state cleanup
    // Only redirect if we're not already on the auth page
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
      console.log('[auth-cache] Redirecting to login page...');
      // Carry the current page through re-auth so the user resumes where
      // they were (e.g. mid-onboarding) instead of landing on the dashboard.
      const returnTo = encodeURIComponent(
        `${window.location.pathname}${window.location.search}`
      );
      setTimeout(() => {
        window.location.href = `/auth?tab=login&reason=session_expired&redirectTo=${returnTo}`;
        // Reset the flag after redirect starts
        setTimeout(() => { isHandlingFatalError = false; }, 1000);
      }, 100);
    } else {
      isHandlingFatalError = false;
    }
  } catch (cleanupError) {
    console.error('[auth-cache] Error during fatal error cleanup:', cleanupError);
    isHandlingFatalError = false;
  }
}

export function createBrowserSupabaseClient(): BrowserClient {
  const url = resolveEnv(process.env.NEXT_PUBLIC_SUPABASE_URL);
  const anonKey = resolveEnv(
    process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
  );
  const cookieDomain = resolveEnv(process.env.NEXT_PUBLIC_COOKIE_DOMAIN);

  if (!url || !anonKey) {
    throw new Error(
      "Supabase environment variables are not configured. Define NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY (or NEXT_PUBLIC_SUPABASE_ANON_KEY).",
    );
  }

  const client = createBrowserClient<Database>(url, anonKey, {
    auth: {
      flowType: "pkce",
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
    // Cookie configuration for session persistence across deployments
    cookieOptions: {
      // Use explicit domain if configured (e.g., ".buildforce.io")
      // This ensures cookies persist across Vercel deployments
      ...(cookieDomain ? { domain: cookieDomain } : {}),
      // Secure cookies in production
      secure: process.env.NODE_ENV === "production",
      // Allow cookies to be sent with same-site requests
      sameSite: "lax",
      // Persist for 7 days. Session cookies (maxAge: undefined) survive
      // most browser sessions but can be cleared aggressively after long
      // idle periods or by some browser policies — and combined with the
      // refresh_token_already_used race, that compounded the
      // logout-on-deploy issue. 7 days gives a comfortable refresh window;
      // Supabase auto-refreshes the access token within it.
      maxAge: 60 * 60 * 24 * 7,
      // Root path so cookies work across all routes
      path: "/",
    },
  });

  // Wrap auth methods to prevent redundant calls
  wrapAuthMethodsWithCache(client);

  return client;
}

/**
 * Wraps Supabase auth methods with caching to prevent redundant calls
 * This is the key fix for the token refresh storm
 *
 * Key features:
 * 1. Separate promises for getUser and getSession to prevent cross-contamination
 * 2. Cache validity check before returning cached data
 * 3. Concurrent request deduplication via shared promise
 * 4. Cache cleared on auth state changes
 */
function wrapAuthMethodsWithCache(client: BrowserClient) {
  const originalGetUser = client.auth.getUser.bind(client.auth);
  const originalGetSession = client.auth.getSession.bind(client.auth);

  // Log once that auth caching is active (helps with debugging)
  if (!authCacheDebugLogged && typeof window !== 'undefined') {
    console.log('[auth-cache] Auth call caching enabled to prevent token refresh storms');
    authCacheDebugLogged = true;
  }

  // Wrap getUser with caching
  client.auth.getUser = async () => {
    const cache = getAuthCache();
    const now = Date.now();

    // Return cached result if still valid
    if (cache.user && now - cache.timestamp < AUTH_CACHE_TTL_MS) {
      return { data: { user: cache.user }, error: null };
    }

    // CRITICAL: If we recently had a rate limit error, don't retry
    // This prevents the retry storm that causes UI refresh loops
    if (cache.lastError && now - cache.lastErrorTimestamp < AUTH_ERROR_COOLDOWN_MS) {
      const errorMessage = cache.lastError.message || 'Auth rate limited';
      console.warn(`[auth-cache] Auth request blocked - in cooldown period after error: ${errorMessage}`);
      // Return cached user if we have one, otherwise return empty
      if (cache.user) {
        return { data: { user: cache.user }, error: null };
      }
      // Return the cached error to prevent retry storms
      return { data: { user: null }, error: cache.lastError };
    }

    // If a getUser request is in progress, wait for it (deduplication)
    if (cache.getUserPromise) {
      try {
        const result = await cache.getUserPromise;
        return result;
      } catch (promiseError) {
        // CRITICAL FIX: Don't fall through blindly - check error cooldown first
        // This prevents the token refresh storm where all waiters create new requests
        // after getting "refresh_token_already_used" error
        const nowAfterError = Date.now();
        const currentCache = getAuthCache();
        if (currentCache.lastError && nowAfterError - currentCache.lastErrorTimestamp < AUTH_ERROR_COOLDOWN_MS) {
          console.warn('[auth-cache] Promise failed but in cooldown, returning cached error');
          return { data: { user: currentCache.user }, error: currentCache.lastError };
        }
        // Only fall through to make a new request if not in cooldown
      }
    }

    // CRITICAL: Create and store the promise synchronously BEFORE awaiting
    // This prevents race conditions where multiple calls slip through before the promise is set
    const makeRequest = async () => {
      const c = getAuthCache(); // Re-get cache in async context
      try {
        const result = await originalGetUser();

        // CRITICAL: Supabase returns errors in result.error, NOT by throwing
        // We must check for errors here and handle them properly
        if (result.error) {
          const authError = result.error as Error;
          c.lastError = authError;
          c.lastErrorTimestamp = Date.now();

          // Check error type and log appropriately
          if (isRateLimitError(authError)) {
            console.error('[auth-cache] RATE LIMITED (429) - entering extended cooldown:', authError.message);
            // Extend cooldown for rate limits to 2 minutes
            c.lastErrorTimestamp = Date.now() + 60000; // Extra 60s on top of normal cooldown
          } else if (isFatalAuthError(authError)) {
            recordFatalAuthError(authError);
          } else {
            console.error('[auth-cache] Auth request returned error, entering cooldown:', authError.message);
          }

          // Still return the result (with error) so callers can handle it
          return result;
        }

        c.user = result.data.user;
        c.timestamp = Date.now();
        // Clear error state on success
        c.lastError = null;
        c.lastErrorTimestamp = 0;
        c.consecutiveFatalErrors = 0;
        c.firstFatalErrorTimestamp = 0;
        return result;
      } catch (error) {
        // This catch is for network errors or unexpected exceptions
        const authError = error as Error;
        c.lastError = authError;
        c.lastErrorTimestamp = Date.now();
        console.error('[auth-cache] Auth request threw exception, entering cooldown:', authError.message);

        if (isFatalAuthError(authError)) {
          recordFatalAuthError(authError);
        }

        throw error;
      } finally {
        // Always clear the promise when done (success or failure)
        c.getUserPromise = null;
      }
    };

    // Store promise IMMEDIATELY (synchronously) before any async operation
    const requestPromise = makeRequest();
    cache.getUserPromise = requestPromise;
    return requestPromise;
  };

  // Wrap getSession with caching
  client.auth.getSession = async () => {
    const cache = getAuthCache();
    const now = Date.now();

    // Return cached result if still valid
    if (cache.session && now - cache.timestamp < AUTH_CACHE_TTL_MS) {
      return { data: { session: cache.session }, error: null };
    }

    // CRITICAL: If we recently had a rate limit error, don't retry
    // This prevents the retry storm that causes UI refresh loops
    if (cache.lastError && now - cache.lastErrorTimestamp < AUTH_ERROR_COOLDOWN_MS) {
      const errorMessage = cache.lastError.message || 'Auth rate limited';
      console.warn(`[auth-cache] Session request blocked - in cooldown period after error: ${errorMessage}`);
      // Return cached session if we have one, otherwise return empty
      if (cache.session) {
        return { data: { session: cache.session }, error: null };
      }
      // Return the cached error to prevent retry storms
      return { data: { session: null }, error: cache.lastError };
    }

    // If a getSession request is in progress, wait for it (deduplication)
    if (cache.getSessionPromise) {
      try {
        const result = await cache.getSessionPromise;
        return result;
      } catch (promiseError) {
        // CRITICAL FIX: Don't fall through blindly - check error cooldown first
        // This prevents the token refresh storm where all waiters create new requests
        // after getting "refresh_token_already_used" error
        const nowAfterError = Date.now();
        const currentCache = getAuthCache();
        if (currentCache.lastError && nowAfterError - currentCache.lastErrorTimestamp < AUTH_ERROR_COOLDOWN_MS) {
          console.warn('[auth-cache] Session promise failed but in cooldown, returning cached error');
          return { data: { session: currentCache.session }, error: currentCache.lastError };
        }
        // Only fall through to make a new request if not in cooldown
      }
    }

    // CRITICAL: Create and store the promise synchronously BEFORE awaiting
    // This prevents race conditions where multiple calls slip through before the promise is set
    const makeRequest = async () => {
      const c = getAuthCache(); // Re-get cache in async context
      try {
        const result = await originalGetSession();

        // CRITICAL: Supabase returns errors in result.error, NOT by throwing
        // We must check for errors here and handle them properly
        if (result.error) {
          const authError = result.error as Error;
          c.lastError = authError;
          c.lastErrorTimestamp = Date.now();

          // Check error type and log appropriately
          if (isRateLimitError(authError)) {
            console.error('[auth-cache] RATE LIMITED (429) - entering extended cooldown:', authError.message);
            // Extend cooldown for rate limits to 2 minutes
            c.lastErrorTimestamp = Date.now() + 60000; // Extra 60s on top of normal cooldown
          } else if (isFatalAuthError(authError)) {
            recordFatalAuthError(authError);
          } else {
            console.error('[auth-cache] Session request returned error, entering cooldown:', authError.message);
          }

          // Still return the result (with error) so callers can handle it
          return result;
        }

        c.session = result.data.session;
        c.user = result.data.session?.user || null;
        c.timestamp = Date.now();
        // Clear error state on success
        c.lastError = null;
        c.lastErrorTimestamp = 0;
        c.consecutiveFatalErrors = 0;
        c.firstFatalErrorTimestamp = 0;
        return result;
      } catch (error) {
        // This catch is for network errors or unexpected exceptions
        const authError = error as Error;
        c.lastError = authError;
        c.lastErrorTimestamp = Date.now();
        console.error('[auth-cache] Session request threw exception, entering cooldown:', authError.message);

        if (isFatalAuthError(authError)) {
          recordFatalAuthError(authError);
        }

        throw error;
      } finally {
        // Always clear the promise when done (success or failure)
        c.getSessionPromise = null;
      }
    };

    // Store promise IMMEDIATELY (synchronously) before any async operation
    const requestPromise = makeRequest();
    cache.getSessionPromise = requestPromise;
    return requestPromise;
  };

  // Clear cache on auth state changes
  client.auth.onAuthStateChange((event, session) => {
    const cache = getAuthCache();
    if (event === "SIGNED_OUT") {
      cache.user = null;
      cache.session = null;
      cache.timestamp = 0;
      cache.getUserPromise = null;
      cache.getSessionPromise = null;
      // Keep error state - user might need to wait before retrying login
    } else if (session) {
      cache.session = session;
      cache.user = session.user;
      cache.timestamp = Date.now();
      // Clear error state on successful auth
      cache.lastError = null;
      cache.lastErrorTimestamp = 0;
      cache.consecutiveFatalErrors = 0;
      cache.firstFatalErrorTimestamp = 0;
    }
  });
}

// Use a module-level variable for initial check
let browserClient: BrowserClient | undefined;

// Symbol key for window-level singleton (survives HMR and module re-evaluation)
const SUPABASE_CLIENT_KEY = '__buildforce_supabase_client__';

export function getBrowserSupabaseClient(): BrowserClient {
  // In browser, check window for existing client (survives module re-evaluation/HMR)
  if (typeof window !== 'undefined') {
    const existingClient = (window as any)[SUPABASE_CLIENT_KEY];
    if (existingClient) {
      browserClient = existingClient;
      return existingClient;
    }
  }

  // Create new client if none exists
  if (!browserClient) {
    browserClient = createBrowserSupabaseClient();

    // Store on window for persistence across module re-evaluations
    if (typeof window !== 'undefined') {
      (window as any)[SUPABASE_CLIENT_KEY] = browserClient;
    }
  }

  return browserClient;
}
